import { EventSource } from "eventsource";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, onScopeDispose, ref, watch } from "vue";

import { useEditManager } from "@/composables/edit-manager";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useWorktreeStore } from "@/composables/worktree-manager";
import config from "@/config";
import { sourceOf } from "@/lib/connectors/provenance";
import { downloadBlob } from "@/lib/download";
import { api, type types } from "@/lib/openapi";
// From the leaf module: the specs mock "@/lib/openapi" down to `api`.
import { isHttpExceptionModel } from "@/lib/openapi/errors";
import { ThreadContextKind, ThreadKind } from "@/lib/openapi/gen";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { notifyEntityChanged } from "@/stores/cache-invalidation";
import { useReleaseStore } from "@/stores/release-store";
import { entityChangesFromEntries } from "@/stores/thread-entry-invalidation";
import { useUserManager } from "@/stores/user-store";

export type ThreadSchema = types.ThreadSchema;
export type ThreadEntrySchema = types.ThreadEntrySchema;
export type ThreadWithEntriesSchema = types.ThreadWithEntriesSchema;
export type ConflictRecordSchema = types.ConflictRecordSchema;
export type EntityVersionsSchema = types.EntityVersionsSchema;
export type UserChangeSchema = types.UserChangeSchema;
export type AffectedEntitySchema = types.AffectedEntitySchema;

export type ConflictResolution = "keep_main" | "keep_thread" | "edited";

export interface AffectedEntity {
    entity_type: string;
    entity_id: string;
}

/** A node created by the agent during the current run, from a live `entity_created` SSE event. */
export interface LiveCreatedEntity {
    entity_type: string;
    entity_id: string;
    entity_name: string;
    parent_id: string | null;
}

/**
 * Outcome of an accept/reject. `conflict` means the accept was gated because the
 * thread is behind main: the backend recorded pick-a-side conflict records during
 * the forced catch-up, the active thread has been refetched so they're now on
 * `conflicts`, and the caller should show the resolution view instead of closing.
 */
export type ResolveOutcome = "resolved" | "conflict" | "error";

/**
 * The OpenAPI client is configured `throwOnError`, so a non-2xx response REJECTS
 * with the parsed error body rather than resolving. A backend DomainError
 * serialises as `{ detail: [{ type, message }] }`; a blocked accept on a
 * behind-main thread surfaces `ErrorType.CONFLICT` (`type === "CONFLICT"`).
 */
interface DomainErrorBody {
    detail: Array<{ type?: unknown; message?: unknown }>;
}

/** A proposal answered by something else, most often auto apply, 409s. */
const isAlreadyAnswered = (error: unknown): boolean =>
    typeof (error as { detail?: unknown })?.detail === "string" &&
    (error as { detail: string }).detail.includes("no longer open");

const asDomainErrorBody = (error: unknown): DomainErrorBody | undefined => {
    if (
        error != null &&
        typeof error === "object" &&
        Array.isArray((error as { detail?: unknown }).detail)
    ) {
        return error as DomainErrorBody;
    }
    return undefined;
};

const isConflictError = (error: unknown): boolean =>
    asDomainErrorBody(error)?.detail.some((d) => d.type === "CONFLICT") ??
    false;

const domainErrorMessage = (error: unknown): string | undefined => {
    const message = asDomainErrorBody(error)?.detail[0]?.message;
    return typeof message === "string" ? message : undefined;
};

export const useThreadsStore = defineStore("threads", () => {
    const toast = useToast();
    const { isViewOnly } = useEditManager();
    const threads = ref<ThreadSchema[]>([]);
    const activeThread = ref<ThreadSchema | undefined>(undefined);
    // Which thread the user is looking at, for the stek-wide unread check.
    // Separate from `activeThread`: that one is the loaded conversation, and
    // connectToThread() disconnects before the detail page's fetch has been
    // painted. Clearing the loaded thread there blanks the page.
    const viewingThreadId = ref<string | undefined>(undefined);
    const activeEntries = ref<ThreadEntrySchema[]>([]);
    const isLoading = ref(false);
    /** A single-thread read into the view is in flight. */
    const isFetching = ref(false);
    /** A `fetchThreads` listing is in flight. */
    const isFetchingList = ref(false);
    // The name of the file currently being uploaded, or null when idle — drives
    // the "Uploading …" chip in the composer's attachments strip.
    const uploadingFileName = ref<string | null>(null);

    // Driven by SSE agent_started / agent_finished events, and seeded from the thread's
    // has_pending_agent_run on every full fetch (initial load + reconnect catch-up).
    const isWaitingForAgent = ref(false);

    // Live progress for the current agent run: each tool the agent invokes emits an
    // `agent_progress` step. These are transient placeholders shown while waiting; they
    // are cleared on `agent_finished`, at which point the persisted activity entries
    // (fetched via fetchThread) replace them — so each action is shown once, never twice.
    const agentProgressSteps = ref<string[]>([]);
    const agentRunStartedAt = ref<number | null>(null);

    // Entities the agent has created so far THIS run, from live `entity_created`
    // SSE events — provisional, for a live-growing tree UI (onboarding). Reset on
    // `agent_started`/thread switch/disconnect and cleared on `agent_finished`,
    // where the worktree resync becomes the authoritative source; a rolled-back
    // run is self-correcting because that clear runs on every exit path.
    const liveCreatedEntities = ref<LiveCreatedEntity[]>([]);

    // The proposal whose Confirm/Cancel the user just clicked. Set synchronously on
    // click so its approval buttons hide immediately (in sync with the "agent is
    // working" indicator) instead of a network round-trip later; cleared if the
    // answer fails so the buttons reappear for a retry. A stale id after a
    // successful answer is harmless — that entry is never the last one again.
    const respondingProposalId = ref<string | null>(null);

    // --- Catch-up conflicts (pick-a-side resolution) ---
    // A thread that is behind main and collided on catch-up carries conflict records;
    // each names the precise entity that needs a pick-a-side resolution before accept
    // can unblock. Exposed off the active thread so the drawer banner + per-conflict
    // rows render straight from store state.
    const conflicts = computed<ConflictRecordSchema[]>(
        () => activeThread.value?.conflicts ?? []
    );
    const pendingConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "pending")
    );
    // Resolved-but-not-accepted records: decisions stay revertible until
    // Accept (869dyyzh9), so the drawer keeps a review path to the Reconcile
    // page as long as any exist.
    const resolvedConflicts = computed<ConflictRecordSchema[]>(() =>
        conflicts.value.filter((c) => c.status === "resolved")
    );
    const hasPendingConflicts = computed(
        () => pendingConflicts.value.length > 0
    );

    // --- Non-agent (user) branch edits ---
    // Entities a user changed directly on the thread branch (NOT the facilitator
    // agent). Surfaced in the resolve dialog so a reviewer sees the non-agent
    // footprint they are about to accept/reject. Lazily fetched (the detection is
    // gitaly-heavy) when the dialog opens, not on every thread poll.
    const userChanges = ref<UserChangeSchema[]>([]);

    // --- Real-time updates via SSE (replaces the old polling loop) ---
    let eventSource: EventSource | null = null;
    // The thread the stream above belongs to, and so the one whose agent run
    // the lost-finish watchdog below polls for. A ref, not a plain let, because
    // the watchdog has to arm on whichever of this and the indicator flag
    // settles last — see the watcher.
    const connectedThreadId = ref<string | null>(null);

    const lastSequence = (): number =>
        activeEntries.value.reduce(
            (max, e) => Math.max(max, e.sequence ?? 0),
            0
        );

    /**
     * The sequence an agent run's entries start after, for the newest run.
     *
     * A run is kicked off by a user's message, so everything after the last one
     * belongs to the most recent run. Used to decide how much of a thread's
     * replayed history counts as already-seen: everything up to and including
     * that message does, the run's own writes do not.
     *
     * Falls back to `maxSeq` — treat it all as history — when there is no user
     * message to anchor on, which keeps an unrecognised entry shape quiet
     * rather than making it announce the whole thread.
     */
    const sequenceBeforeNewestRun = (
        entries: readonly ThreadEntrySchema[],
        maxSeq: number
    ): number => {
        let latest: number | undefined;
        for (const entry of entries) {
            if (entry.type !== "contribution") continue;
            if (entry.source?.type !== "user") continue;
            const sequence = entry.sequence ?? 0;
            if (latest === undefined || sequence > latest) latest = sequence;
        }
        return latest ?? maxSeq;
    };

    // The highest entry sequence whose entity changes have been announced to the
    // cache-invalidation bus, per thread. Entries are replayed wholesale on a
    // reconnect and on every full read, so without this the same run would be
    // announced again and again — and opening a thread at all would replay every
    // action it ever recorded. A map rather than one slot: the drawer can be
    // switched to another thread and back while the PAGE stays on the first
    // one's branch, and a forgotten watermark there would silently drop
    // everything the agent wrote in between. Deliberately NOT cleared on
    // disconnect, so closing and reopening the drawer does not replay.
    const announcedThrough = new Map<string, number>();
    // Whether anything has been announced since the current run started. Read
    // by `agent_finished` to decide whether the run still needs its
    // manifest-based fallback. A per-run flag rather than "what did the last
    // call announce": the server publishes `new_entry` BEFORE `agent_finished`,
    // so by the time the fallback looks, the run's own changes have already
    // been announced by the delta fetch and any later read legitimately finds
    // nothing fresh.
    let announcedThisRun = false;

    /**
     * Tell the cache-invalidation bus what the agent just wrote on the branch.
     *
     * The agent commits at the SAME `thread/<id>` revision the open pages are
     * already reading, so a revision-keyed refetch cannot see the write and the
     * page stays stale until a manual refresh. The action entries that arrive
     * over SSE already name every entity the run touched, so they are what the
     * caches get told about.
     *
     * `isFullRead` says whether `entries` is the thread's whole history or just
     * a delta. Only a full read may seed the watermark, because seeding means
     * "most of this is already-known history" — doing that to a delta would
     * swallow the very entries it exists to announce.
     *
     * Returns the number of changes announced.
     */
    const announceEntryChanges = (
        threadId: string,
        entries: readonly ThreadEntrySchema[],
        { isFullRead }: { isFullRead: boolean }
    ): number => {
        // A late read for a thread the viewer has since left: its writes are not
        // what is on screen.
        if (activeThread.value?.identity !== threadId) return 0;

        const maxSeq = entries.reduce(
            (max, e) => Math.max(max, e.sequence ?? 0),
            0
        );

        const seen = announcedThrough.get(threadId);
        // First full read of this thread: everything in hand is history the
        // server replayed, much of it from runs that finished days ago, so
        // announcing all of it would refetch half the app. Seed past all of it
        // EXCEPT the newest run — that one may well have finished while the
        // drawer was closed, in which case the page behind the drawer is still
        // showing what the branch held before it, and this read is the only
        // chance to say so.
        const since =
            seen ?? (isFullRead ? sequenceBeforeNewestRun(entries, maxSeq) : 0);
        const fresh = entries.filter((e) => (e.sequence ?? 0) > since);
        // Advance before notifying: a handler that re-enters this store must not
        // find the same entries still looking unannounced.
        announcedThrough.set(threadId, Math.max(since, maxSeq));

        // The page is rendering another revision (master, or another thread's
        // branch), so these branch writes are not what it is showing and
        // refetching would be pure churn.
        if (useReleaseStore().activeThreadId !== threadId) return 0;

        const changes = entityChangesFromEntries(fresh);
        // One batched call: the bus dedupes per batch, so each cache refetches
        // once for the whole run rather than once per action.
        if (changes.length) {
            notifyEntityChanged(changes);
            // Not armed by the seeding read: what it announces is the newest
            // run, which has usually already finished. A drawer opened DURING a
            // run would otherwise disarm that run's fallback on the strength of
            // the previous run's writes.
            if (seen !== undefined) announcedThisRun = true;
        }
        return changes.length;
    };

    /**
     * Wind up a finished run: hide the indicator, drop the live placeholders,
     * and pull the answer in.
     *
     * Reached two ways — the `agent_finished` event, and the watchdog below
     * when that event never arrives — so it must be safe to run twice for one
     * run. It is: every step is idempotent, and the re-read is authoritative.
     */
    const settleFinishedRun = (threadId: string) => {
        isWaitingForAgent.value = false;
        // Clear the live steps: the persisted activity entries fetched
        // below replace them, so nothing is shown twice. The worktree
        // resync below becomes authoritative for the live-growing
        // tree too, so any phantom nodes from a rolled-back run drop
        // here rather than lingering.
        agentProgressSteps.value = [];
        agentRunStartedAt.value = null;
        liveCreatedEntities.value = [];
        // The agent may have committed entity changes on the branch at
        // the SAME thread/<id> revision. Re-sync the worktree (diff
        // badges), then refetch the thread. The run's own action
        // entries were already announced by the delta fetch the
        // preceding `new_entry` triggered — the server publishes it
        // before this event — so this read normally finds nothing
        // fresh and announces nothing.
        void useWorktreeStore().sync();
        void (async () => {
            try {
                if (!(await refreshThread(threadId))) return;
                // Fallback for writes that leave no action entry of
                // their own — create_domain's auto-created admin role,
                // for one. The manifest only covers domain/product/
                // process, so it cannot replace the entry-derived
                // path; it only fills the gap it can see.
                if (announcedThisRun) return;
                // Silent for the same reason the entry-derived path
                // is: the page is reading another revision, so the
                // branch's writes are not what it is showing.
                if (useReleaseStore().activeThreadId !== threadId) {
                    return;
                }
                const affected = activeThread.value?.affected_entities ?? [];
                if (affected.length > 0) {
                    notifyEntityChanged(
                        affected.map((e) => ({
                            entityType: e.entity_type,
                            entityId: e.entity_id,
                        }))
                    );
                }
            } catch {
                // Best-effort; the worktree sync above already
                // refreshed the diff badges.
            } finally {
                // Consume the flag, on every exit path. A tab that
                // joins mid-run never sees `agent_started` to arm
                // it, so a value left set here would suppress the
                // NEXT run's fallback.
                announcedThisRun = false;
            }
        })();
    };

    // --- Lost-finish watchdog ---
    // The indicator is driven purely by events: `agent_finished` is what hides
    // it. One lost event — a dropped stream, a Valkey hiccup, a worker going
    // away mid-drain — therefore leaves it up indefinitely, because nothing
    // else ever asks. The failsafe TTL behind `has_pending_agent_run` does not
    // save us: it expires the server's flag, and no one reads it.
    //
    // So while the indicator is up, ask. The server's flag is authoritative and
    // already expires on its own; when it says the run is done, settle the run
    // ourselves. A lost event then costs a beat instead of a page reload.
    const AGENT_WATCHDOG_INTERVAL_MS = 30_000;
    /** Probes that must agree the run is finished before the indicator comes down. */
    const AGENT_WATCHDOG_FINISHED_PROBES = 2;
    let agentWatchdog: ReturnType<typeof setInterval> | null = null;

    const stopAgentWatchdog = () => {
        if (agentWatchdog !== null) {
            clearInterval(agentWatchdog);
            agentWatchdog = null;
        }
    };

    const startAgentWatchdog = (threadId: string) => {
        stopAgentWatchdog();
        // Consecutive probes that have reported the run finished. Two are
        // required before acting, because the server answers "not pending"
        // for a live run as readily as a finished one: `is_active` turns a
        // Valkey read failure into a plain False. One blip would otherwise
        // tear the indicator down mid-run — and because this polls, that is
        // reachable every 30s for the whole run rather than only on a reload.
        //
        // The trade is lopsided in our favour. Taking 60s instead of 30s to
        // recover a lost event is barely perceptible; clearing a run that is
        // still going is loud, and reads as the request having been dropped.
        // Where it is unsure, the backstop should hesitate rather than act.
        //
        // Note this does nothing for a run whose keys have genuinely expired
        // mid-flight (a turn silent longer than their TTL): that reads
        // finished on every probe, not just one. See 869f8wjvm.
        let finishedProbes = 0;
        agentWatchdog = setInterval(() => {
            void (async () => {
                // Deliberately the cheap read: no entries, and no footprint —
                // the footprint is a whole worktree build, far too costly to
                // poll. `has_pending_agent_run` is filled in regardless.
                const fresh = await fetchThreadSummary(threadId, {
                    withFootprint: false,
                });
                // The read above can outlive the thing it asked about: the
                // user may have switched threads, or this run may already have
                // been settled and another started. Acting on a stale answer
                // would pull down an indicator belonging to something else, so
                // re-check that the world still matches what we probed for.
                if (
                    connectedThreadId.value !== threadId ||
                    !isWaitingForAgent.value
                ) {
                    return;
                }
                // A failed read tells us nothing about the run; leave the
                // indicator alone and try again on the next tick. It does not
                // count as evidence either way, so the streak survives it.
                if (!fresh) return;
                if (fresh.has_pending_agent_run) {
                    finishedProbes = 0;
                    return;
                }
                if (++finishedProbes < AGENT_WATCHDOG_FINISHED_PROBES) return;
                settleFinishedRun(threadId);
            })();
        }, AGENT_WATCHDOG_INTERVAL_MS);
    };

    // Driving this off the flag rather than the event covers every way a run
    // starts — the SSE event and the optimistic sets at click time alike —
    // and `settleFinishedRun` clearing the flag is what stops the timer.
    //
    // Both sources are watched because the two thread surfaces set them in
    // opposite orders: the drawer connects and then seeds the flag, while the
    // full-page route fetches first (seeding the flag) and connects after.
    // Watching the flag alone arms only the drawer — on the page the callback
    // runs while the thread id is still null and never fires again, which is
    // precisely the surface a user lands on after the refresh this bug forces.
    watch([isWaitingForAgent, connectedThreadId], ([waiting, threadId]) => {
        if (waiting && threadId) startAgentWatchdog(threadId);
        else stopAgentWatchdog();
    });

    // A run still in flight when the store goes away — HMR, or a fresh pinia
    // between tests — would otherwise leave an interval behind, polling against
    // a store nobody holds any more. `disconnectThread` only covers the paths
    // that close the stream first.
    onScopeDispose(stopAgentWatchdog);

    const disconnectThread = () => {
        if (eventSource) {
            eventSource.close();
            eventSource = null;
        }
        // Deliberately no stopAgentWatchdog() here: the watcher owns the
        // timer's lifecycle, and nulling the id above is what stops it.
        // Stopping it here as well breaks the reconnect-to-the-same-thread
        // case — connectToThread starts by disconnecting, so the id would go
        // out and straight back, leaving the watcher nothing net-changed to
        // react to while the timer had already been torn down.
        connectedThreadId.value = null;
        liveCreatedEntities.value = [];
        viewingThreadId.value = undefined;
    };

    const connectToThread = (threadId: string) => {
        // Tear down any previous connection (e.g. switching threads).
        disconnectThread();
        viewingThreadId.value = threadId;

        // Opening a conversation follows it and marks it read — the reason to
        // look is satisfied even before any new content streams in.
        void markThreadRead(threadId);
        ensureNotificationPermission();
        // The thread whose run the watchdog is responsible for. Not
        // `viewedThreadId`: that is set by the readers, so it is empty on the
        // paths that connect without one and would silently leave the
        // watchdog off for exactly the run it exists to catch.
        connectedThreadId.value = threadId;

        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        const source = new EventSource(
            `${config.bekendURI}/thread/events/${threadId}`,
            {
                // Resolve the token *inside* the fetch override: the eventsource lib reuses
                // this closure on every auto-reconnect, and the SSE endpoint re-authorizes on
                // each reconnect. Firebase ID tokens expire ~1h, so capturing once would let
                // the stream silently 401 after expiry. getToken() returns a cached token and
                // auto-refreshes near expiry, so this stays cheap.
                fetch: async (input, init) => {
                    const token = await getToken();
                    return fetch(input, {
                        ...init,
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${token}`,
                            Organization: organization?.value?.identity ?? "",
                            Stek: stek?.value?.identity ?? "",
                        },
                    });
                },
            }
        );
        eventSource = source;

        // Fires on initial connect and on every auto-reconnect — full fetch to catch up
        // on anything missed while disconnected (pub/sub is fire-and-forget).
        source.addEventListener("open", () => {
            void refreshThread(threadId);
        });

        source.addEventListener("message", ({ data }) => {
            let event: { type?: string };
            try {
                event = JSON.parse(data);
            } catch {
                return;
            }
            switch (event.type) {
                case "new_entry":
                    void fetchNewEntries(threadId);
                    break;
                case "agent_started":
                    isWaitingForAgent.value = true;
                    agentProgressSteps.value = [];
                    liveCreatedEntities.value = [];
                    // A fresh run: nothing announced for it yet, so the
                    // fallback below is armed again.
                    announcedThisRun = false;
                    // Don't reset the elapsed clock if it's already running for this
                    // run (an optimistic set may have started it at click time);
                    // agent_finished clears it.
                    if (agentRunStartedAt.value === null) {
                        agentRunStartedAt.value = Date.now();
                    }
                    break;
                case "agent_progress": {
                    const message = (event as { message?: string }).message;
                    if (message) {
                        agentProgressSteps.value = [
                            ...agentProgressSteps.value,
                            message,
                        ];
                    }
                    break;
                }
                case "entity_created": {
                    const created = event as {
                        entity_type?: string;
                        entity_id?: string;
                        entity_name?: string;
                        parent_id?: string | null;
                    };
                    if (
                        created.entity_type &&
                        created.entity_id &&
                        created.entity_name &&
                        !liveCreatedEntities.value.some(
                            (e) => e.entity_id === created.entity_id
                        )
                    ) {
                        liveCreatedEntities.value = [
                            ...liveCreatedEntities.value,
                            {
                                entity_type: created.entity_type,
                                entity_id: created.entity_id,
                                entity_name: created.entity_name,
                                parent_id: created.parent_id ?? null,
                            },
                        ];
                    }
                    break;
                }
                case "agent_finished": {
                    settleFinishedRun(threadId);
                    break;
                }
                case "thread_resolved":
                    void refreshThread(threadId);
                    void fetchThreads();
                    disconnectThread();
                    break;
            }
        });

        // EventSource auto-reconnects on transient errors (and 'open' re-fires, triggering a
        // catch-up fetch), but it does NOT reconnect on a fatal response such as a 403 — so we
        // log without claiming a retry, making a permanent failure visible rather than letting
        // updates silently stall. (A user-facing reconnecting/disconnected indicator that can
        // distinguish the two states would be a good follow-up.)
        source.addEventListener("error", () => {
            console.warn(
                `Thread SSE connection error (thread ${threadId}); ` +
                    `the browser reconnects if it was transient.`
            );
        });
    };

    // --- Unread badges + in-tab notifications (stek-wide, any thread) ---
    // Separate from `eventSource` above (the per-thread conversation stream):
    // this one has no thread id and stays connected whenever a stek is active,
    // regardless of whether the drawer/detail page has a thread open.
    let stekEventSource: EventSource | null = null;

    const findThreadIndex = (threadId: string): number =>
        threads.value.findIndex((t) => t.identity === threadId);

    const setLocalUnreadCount = (threadId: string, count: number) => {
        const idx = findThreadIndex(threadId);
        if (idx !== -1) {
            threads.value[idx] = { ...threads.value[idx], unread_count: count };
        }
        if (activeThread.value?.identity === threadId) {
            activeThread.value = { ...activeThread.value, unread_count: count };
        }
    };

    const bumpLocalUnreadCount = (threadId: string) => {
        const idx = findThreadIndex(threadId);
        if (idx === -1) {
            // A thread we don't know about locally yet (e.g. just became a
            // participant) — pull the list so it shows up with a correct count
            // rather than guessing at "1".
            void fetchThreads();
            return;
        }
        const current = threads.value[idx].unread_count ?? 0;
        threads.value[idx] = {
            ...threads.value[idx],
            unread_count: current + 1,
        };
    };

    /** Sum of unread chat messages across every thread — drives the top-bar badge. */
    const unreadTotal = computed(() =>
        threads.value.reduce((sum, t) => sum + (t.unread_count ?? 0), 0)
    );

    /**
     * Follow the thread (if not already) and mark it read up to its latest
     * entry. Called when its conversation is opened, and again whenever a
     * `new_entry` arrives while it stays the actively-viewed thread. Optimistic:
     * zeroes the local count immediately rather than waiting on the round-trip.
     */
    const markThreadRead = async (threadId: string) => {
        setLocalUnreadCount(threadId, 0);
        try {
            await api.startThreadThreadMgtMarkThreadReadStartPost({
                body: { thread_id: threadId },
            });
        } catch (e) {
            console.warn(`Could not mark thread ${threadId} read`, e);
        }
    };

    // Browser notifications (Notification API — tab must stay open, this is
    // NOT Web Push). Ask once, the first time a thread is opened; PWA etiquette
    // is to never re-prompt once the user has said no.
    const ensureNotificationPermission = () => {
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "default") {
            void Notification.requestPermission();
        }
    };

    const notifyNewMessage = (
        threadId: string,
        title: string,
        body: string | undefined
    ) => {
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "granted") return;
        // `tag` collapses repeats from the same thread into one OS notification
        // instead of stacking a pile of them.
        const notification = new Notification(title, {
            body: body || "New message",
            tag: threadId,
        });
        notification.onclick = () => {
            window.focus();
            openDrawer(threadId);
        };
    };

    const disconnectStekEvents = () => {
        if (stekEventSource) {
            stekEventSource.close();
            stekEventSource = null;
        }
    };

    /**
     * Open the stek-wide SSE stream that badges/notifies for EVERY thread in the
     * stek, not just the one open in a conversation pane. Connected whenever a
     * stek is active and threads are enabled (see ApplicationLayout.vue), independent
     * of the drawer/detail page's per-thread `connectToThread`.
     */
    const connectStekEvents = (stekId: string) => {
        disconnectStekEvents();

        const { getToken } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { user } = useUserManager();

        const source = new EventSource(`${config.bekendURI}/thread/events`, {
            fetch: async (input, init) => {
                const token = await getToken();
                return fetch(input, {
                    ...init,
                    headers: {
                        ...init?.headers,
                        Authorization: `Bearer ${token}`,
                        Organization: organization?.value?.identity ?? "",
                        Stek: stekId,
                    },
                });
            },
        });
        stekEventSource = source;

        source.addEventListener("message", ({ data }) => {
            let event: {
                type?: string;
                thread_id?: string;
                user_id?: string;
                source_id?: string;
                chat_visible?: boolean;
                thread_title?: string;
                preview?: string;
            };
            try {
                event = JSON.parse(data);
            } catch {
                return;
            }
            const threadId = event.thread_id;
            if (!threadId) return;

            switch (event.type) {
                case "new_entry": {
                    // Our own message, from this tab or another one on the same
                    // account — the backend already keeps our own unread at 0.
                    if (
                        user.value?.identity &&
                        event.source_id === user.value.identity
                    ) {
                        break;
                    }
                    const isViewingLive =
                        viewingThreadId.value === threadId && !document.hidden;
                    if (isViewingLive) {
                        // Already looking at it — keep the badge cleared instead
                        // of flashing it on then immediately off.
                        void markThreadRead(threadId);
                    } else if (event.chat_visible) {
                        bumpLocalUnreadCount(threadId);
                        notifyNewMessage(
                            threadId,
                            event.thread_title || "New message",
                            event.preview
                        );
                    }
                    break;
                }
                case "thread_read": {
                    // A second tab of the same account marked it read.
                    if (
                        user.value?.identity &&
                        event.user_id === user.value.identity
                    ) {
                        setLocalUnreadCount(threadId, 0);
                    }
                    break;
                }
            }
        });

        source.addEventListener("error", () => {
            console.warn(
                "Stek-wide thread SSE connection error; the browser reconnects if it was transient."
            );
        });
    };

    /**
     * Map from entity identity to the open threads that affect it.
     * Only includes open threads.
     */
    const entityThreadMap = computed(() => {
        const map = new Map<string, ThreadSchema[]>();
        for (const thread of threads.value) {
            if (thread.status !== "open") continue;
            const affected =
                (
                    thread as ThreadSchema & {
                        affected_entities?: AffectedEntity[];
                    }
                ).affected_entities ?? [];
            for (const ae of affected) {
                const existing = map.get(ae.entity_id) ?? [];
                existing.push(thread);
                map.set(ae.entity_id, existing);
            }
        }
        return map;
    });

    const getThreadsForEntity = (entityId: string): ThreadSchema[] => {
        return entityThreadMap.value.get(entityId) ?? [];
    };

    const hasThreadsForEntity = (entityId: string): boolean => {
        return entityThreadMap.value.has(entityId);
    };

    // Drawer state
    const drawerOpen = ref(false);
    const drawerThreadId = ref<string | undefined>(undefined);
    /**
     * The thread `activeThread`/`activeEntries` belong to: the one the user
     * last opened, from the drawer or from a page (reconcile, thread detail).
     * Not `drawerThreadId` — closing the drawer keeps that selection so the
     * draft bar can reopen the conversation, so it goes stale the moment a
     * page opens a different thread.
     */
    let viewedThreadId: string | undefined;

    const openDrawer = (threadId?: string, thread?: ThreadSchema) => {
        drawerOpen.value = true;
        if (threadId) {
            drawerThreadId.value = threadId;
            viewedThreadId = threadId;
        }
        // Hydrate the drawer shell from a thread we already hold (e.g. one we just
        // created) so its header renders instantly instead of waiting on a fetch;
        // entries stream in via the SSE fetch that opening the drawer triggers.
        if (thread && thread.identity === threadId) {
            activeThread.value = thread;
            activeEntries.value = [];
        }
    };

    /**
     * Open the drawer on the threads *list* (no thread selected). Used by the
     * entity discussion indicators: clicking one shows the list of threads
     * rather than jumping straight into a thread's branch. Clears any
     * previously selected thread so a stale `drawerThreadId` doesn't reopen the
     * detail view; the active branch view (if any) is left untouched.
     */
    const openThreadsList = () => {
        drawerThreadId.value = undefined;
        drawerOpen.value = true;
    };

    /** Whether the last close plays the panel's leave animation. */
    const drawerLeaveAnimated = ref(true);

    const closeDrawer = ({ animate = true }: { animate?: boolean } = {}) => {
        drawerLeaveAnimated.value = animate;
        drawerOpen.value = false;
    };

    const selectThread = (threadId: string | undefined) => {
        drawerThreadId.value = threadId;
        if (threadId) viewedThreadId = threadId;
    };

    /**
     * Threads created while a `list_threads` was already in flight, id → the
     * create's sequence number.
     *
     * `list_threads` diffs every open draft against master, so on a stek with
     * a pile of open drafts it takes seconds — long enough that clicking Edit
     * creates a draft, inserts it locally, and then has it wiped out when the
     * older response lands and replaces the whole array. The draft still
     * exists on the server; the client just stops knowing about it, which
     * leaves the draft bar inert with no title and no working buttons.
     */
    const createsInFlight = new Map<string, number>();
    let createSeq = 0;

    /**
     * The create-sequence of the newest listing whose membership has been
     * applied. The protection above is per-draft and retires as soon as any
     * response mentions the draft, which the drawer's quick listing does while
     * the page-load one is still in flight. A response that went out earlier
     * than one already applied is history as far as WHICH threads exist goes,
     * but its diffs are still the only ones anybody asked for.
     */
    let appliedListingSeq = -1;

    /**
     * Load the thread list.
     *
     * `withDiffs` chooses what the server does, and it is the difference
     * between a prompt answer and tens of seconds. Annotating each thread with
     * `affected_entities` costs a whole worktree build against that thread's
     * branch, done one thread after another — ~1.4s per OPEN thread, so a stek
     * carrying thirty open drafts spends the better part of a minute on it.
     *
     *   true (default)  the full record, diffs included. What the sidebar
     *                   badges and the drawer's page grouping are built from,
     *                   so the page-load fetch keeps asking for it.
     *   false           records only — titles, statuses, timestamps. Enough to
     *                   render the drawer correctly and to notice a draft that
     *                   appeared or was resolved elsewhere.
     *
     * A diff-less response never CLEARS diffs we already hold: each thread
     * keeps whatever `affected_entities` it had. Otherwise a quick refresh
     * would blank every sidebar badge and drop every card out of "On this
     * page" until the slow fetch caught up.
     */
    let openListings = 0;
    const fetchThreads = async ({ withDiffs = true } = {}) => {
        openListings += 1;
        isFetchingList.value = true;
        try {
            await listThreads(withDiffs);
        } finally {
            openListings -= 1;
            if (openListings === 0) isFetchingList.value = false;
        }
    };

    const listThreads = async (withDiffs: boolean) => {
        const seqAtStart = createSeq;
        const { data, response } =
            await api.startThreadThreadMgtListThreadsStartPost({
                body: { include_affected_entities: withDiffs },
            });

        if (response.ok && data) {
            const returned = new Set(data.threads.map((t) => t.identity));
            // Anything this response does not mention and was NOT created
            // after the request went out is simply gone (resolved or deleted
            // elsewhere) — stop protecting it.
            for (const [id, seq] of [...createsInFlight]) {
                if (returned.has(id) || seq <= seqAtStart) {
                    createsInFlight.delete(id);
                }
            }
            // Whatever is left raced this request and would be lost.
            // Read the CURRENT array, not a snapshot from before the await —
            // the raced draft was inserted while the request was open.
            const raced = threads.value.filter(
                (t) =>
                    !returned.has(t.identity) && createsInFlight.has(t.identity)
            );

            let incoming = data.threads;
            if (!withDiffs) {
                const known = new Map(
                    threads.value.map((t) => [t.identity, t.affected_entities])
                );
                incoming = incoming.map((t) => {
                    const previous = known.get(t.identity);
                    return previous?.length
                        ? { ...t, affected_entities: previous }
                        : t;
                });
            }

            if (seqAtStart < appliedListingSeq) {
                if (withDiffs) {
                    const fresh = new Map(
                        data.threads.map((t) => [t.identity, t])
                    );
                    threads.value = threads.value.map((t) => {
                        const diffs = fresh.get(t.identity)?.affected_entities;
                        return diffs?.length
                            ? { ...t, affected_entities: diffs }
                            : t;
                    });
                }
            } else {
                appliedListingSeq = seqAtStart;
                threads.value = raced.length
                    ? [...raced, ...incoming]
                    : incoming;
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Could not load threads",
                detail: "Server returned " + response.status,
            });
        }
    };

    /**
     * Put a freshly-read thread back into the list.
     *
     * The single-thread reads return a footprint the list never saw: the
     * drawer's page grouping and the sidebar badges are built from
     * `affected_entities` on `threads`, and only a full `fetchThreads()`
     * recomputes that — on page load. A draft created or edited since then
     * reads as touching nothing, and sits under "on other pages" until a
     * reload.
     *
     * Replaces, never adds. The list is the set of threads that still exist,
     * and a read can outlive one: the history timeline opens resolved
     * threads, and `deleteThread` takes erased ones out.
     *
     * The footprint alone, not the whole record. `resolveThread` marks the
     * list entry resolved before its own refetch lands, precisely so the card
     * does not go on reading "open" for seconds — writing a whole thread back
     * from a read that overlaps that would undo it.
     */
    const mergeFootprintIntoList = (fresh: ThreadSchema) => {
        if (!fresh?.affected_entities) return;
        threads.value = threads.value.map((t) =>
            t.identity === fresh.identity
                ? { ...t, affected_entities: fresh.affected_entities }
                : t
        );
    };

    let openReads = 0;

    /** Resolves true when the answer landed in the view. */
    const readThreadIntoView = async (threadId: string): Promise<boolean> => {
        openReads += 1;
        isFetching.value = true;
        try {
            const read = await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                },
            });
            if (viewedThreadId !== threadId) return false;
            const applied = applyThreadRead(read);
            if (applied) {
                announceEntryChanges(threadId, read.data?.entries ?? [], {
                    isFullRead: true,
                });
            }
            return applied;
        } finally {
            openReads -= 1;
            if (openReads === 0) isFetching.value = false;
        }
    };

    /**
     * Open a thread: it takes the view from whichever held it. A drawer open
     * on another thread closes, since its conversation would render this
     * thread while still posting to its own.
     */
    const fetchThread = (threadId: string): Promise<boolean> => {
        viewedThreadId = threadId;
        if (drawerOpen.value && drawerThreadId.value !== threadId) {
            closeDrawer();
        }
        return readThreadIntoView(threadId);
    };

    /**
     * Re-read a thread after a write or a live event. It never takes the view
     * from another thread: these land long after the reader may have moved on.
     */
    const refreshThread = async (threadId: string): Promise<boolean> => {
        if (viewedThreadId && viewedThreadId !== threadId) return false;
        viewedThreadId = threadId;
        return readThreadIntoView(threadId);
    };

    const applyThreadRead = ({
        data,
        response,
    }: Awaited<
        ReturnType<typeof api.startThreadThreadMgtGetThreadStartPost>
    >): boolean => {
        if (response.ok && data) {
            activeThread.value = data.thread;
            // This answer already carries the footprint — the server builds it
            // whether or not anyone keeps it.
            mergeFootprintIntoList(data.thread);
            activeEntries.value = data.entries ?? [];
            // Seed the agent indicator AND the in-flight run's replay (start time +
            // steps, recorded server-side) so opening or reconnecting mid-run shows
            // the current activity immediately; live SSE events append on top. The
            // server stores each step before publishing it, so its list is never
            // behind what SSE has delivered — re-seeding here can't drop a step.
            isWaitingForAgent.value =
                data.thread?.has_pending_agent_run ?? false;
            agentProgressSteps.value = data.thread?.agent_progress_steps ?? [];
            // Adopt the server's run-start only when we aren't already timing this
            // run locally, so a mid-run fetch (reconnect, catch-up) can't reset the
            // elapsed clock. Clear it when the server reports no active run.
            const serverRunStartedAt =
                data.thread?.agent_run_started_at ?? null;
            if (serverRunStartedAt === null) {
                agentRunStartedAt.value = null;
            } else if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = serverRunStartedAt;
            }
            return true;
        }
        toast.add({
            severity: "error",
            summary: "Could not load thread",
            detail: "Server returned " + response.status,
        });
        return false;
    };

    /**
     * Read-only fetch of a thread's metadata (title, status, affected entities)
     * WITHOUT touching `activeThread`/`activeEntries` or the drawer. Used by the
     * history timeline to lazily enrich a decision entry when it's expanded, so
     * opening the history tab never disturbs whatever thread the drawer has open.
     * Returns null (silently — enrichment is best-effort) on any failure.
     *
     * `withFootprint: false` drops `affected_entities` from the answer and,
     * with it, the branch diff the server would build to fill it in — a whole
     * worktree read at the thread revision plus a stek read for the display
     * name. Callers that want one field off the manifest (is this thread still
     * open?) should say so; the ones that render the footprint must not.
     */
    const fetchThreadSummary = async (
        threadId: string,
        { withFootprint = true }: { withFootprint?: boolean } = {}
    ): Promise<ThreadSchema | null> => {
        try {
            const { data, response } =
                await api.startThreadThreadMgtGetThreadStartPost({
                    body: {
                        thread_id: threadId,
                        include_entries: false,
                        include_affected_entities: withFootprint,
                    },
                });
            if (response.ok && data) return data.thread;
        } catch {
            // Best-effort: the row falls back to the git-only decision data.
        }
        return null;
    };

    /**
     * Whether a draft can still be written into — for a caller about to write.
     *
     * Four answers, and the last two must not be confused: `gone` (the
     * backend no longer has it) and `closed` (accepted or rejected) mean
     * there is nothing to add to, so a caller may fall back to a new thread;
     * `unreachable` (the read itself failed) means nothing is known, and a
     * caller that treated it as "gone" would create a second draft beside
     * one that is still there. `fetchThreadSummary` folds all of those into
     * `null`, which is fine for the best-effort reads it serves and wrong
     * for this one.
     */
    const readDraftState = async (
        threadId: string
    ): Promise<"open" | "closed" | "gone" | "unreachable"> => {
        try {
            const { data, response } =
                await api.startThreadThreadMgtGetThreadStartPost({
                    body: {
                        thread_id: threadId,
                        include_entries: false,
                        include_affected_entities: false,
                    },
                });
            if (response.ok && data) {
                return data.thread.status === "open" ? "open" : "closed";
            }
            return response.status === 404 ? "gone" : "unreachable";
        } catch (e) {
            // `throwOnError` rejects with the error body; a thread the backend
            // does not have says so by type, anything else is the read failing.
            return isHttpExceptionModel(e) && e.detail[0]?.type === "NOT_FOUND"
                ? "gone"
                : "unreachable";
        }
    };

    /**
     * Re-read one draft's footprint and put it back in the list.
     *
     * Called when a draft is left, which is the moment its footprint has
     * finished changing and the moment before the list is next read. One
     * thread costs the server one worktree build; the alternative is
     * `fetchThreads()`, which pays that for every open draft.
     *
     * Best-effort, and meant to be left unawaited: the grouping being a beat
     * behind is not worth holding a leave for, and the next page load
     * recomputes it regardless.
     */
    const refreshThreadFootprint = async (threadId: string) => {
        const fresh = await fetchThreadSummary(threadId, {
            withFootprint: true,
        });
        if (fresh) mergeFootprintIntoList(fresh);
    };

    /**
     * Delta fetch: pull only entries newer than what we already have and append them.
     * Triggered by a `new_entry` SSE notification — keeps bandwidth proportional to new
     * activity instead of refetching the whole thread.
     */
    const fetchNewEntries = async (threadId: string) => {
        const { data, response } =
            await api.startThreadThreadMgtGetThreadStartPost({
                body: {
                    thread_id: threadId,
                    include_entries: true,
                    since_sequence: lastSequence(),
                },
            });

        if (!response.ok || !data) return;
        // Ignore late deltas for a thread we've since navigated away from.
        if (activeThread.value?.identity !== threadId) return;

        activeThread.value = data.thread;
        const existing = new Set(activeEntries.value.map((e) => e.id));
        const fresh = (data.entries ?? []).filter((e) => !existing.has(e.id));
        if (fresh.length) {
            activeEntries.value = [...activeEntries.value, ...fresh];
        }
        announceEntryChanges(threadId, fresh, { isFullRead: false });
    };

    // Thread id -> the last catch-up's failure message. A failed comparison
    // with main means the recorded conflict set cannot be trusted (often:
    // NONE recorded) — accept surfaces read this to stay blocked instead of
    // presenting "nothing to resolve" and failing at the accept click.
    const catchUpFailures = ref<Record<string, string>>({});

    /**
     * Catch the thread's branch up with main — the open/view interaction trigger
     * (reconciliation model §4). Merges current master into the branch so a forked
     * domain reads fresh and the diff badges are accurate, and a blocked catch-up
     * records conflicts (surfaced via the behind-main banner). Best-effort: a
     * failure must never block viewing the thread, so it never throws — but it
     * is RECORDED (``catchUpFailures``) so accept surfaces can block on it. On
     * success the thread is refetched (to surface any newly-recorded conflicts) and
     * the worktree re-synced (to refresh the diff badges) so the view reflects the
     * post-catch-up state.
     */
    // The generated client's error body is {detail: [{type, message}]} — pull
    // the human message out of whatever shape arrives (a plain Error, a string,
    // the API detail list) so the banner never reads "[object Object]".
    const failureMessage = (reason: unknown): string => {
        if (typeof reason === "string") return reason;
        const detail = (reason as { detail?: { message?: string }[] } | null)
            ?.detail;
        if (Array.isArray(detail)) {
            const messages = detail
                .map((entry) => entry?.message)
                .filter(Boolean);
            if (messages.length) return messages.join("; ");
        }
        const direct = (reason as { message?: string } | null)?.message;
        if (direct) return direct;
        try {
            return JSON.stringify(reason) ?? String(reason);
        } catch {
            return String(reason);
        }
    };

    // The thread's status as far as this store already knows it — no fetch.
    const knownThreadStatus = (threadId: string): string | undefined => {
        if (activeThread.value?.identity === threadId) {
            return activeThread.value.status;
        }
        return threads.value.find((t) => t.identity === threadId)?.status;
    };

    const catchUpThread = async (threadId: string) => {
        // Catching up writes main into the branch, which a viewer may not do
        // (the backend refuses with a 403). They read the branch as it stands.
        if (isViewOnly.value) return;

        // A closed thread has nothing to catch up — viewing an accepted or
        // rejected thread's page must not fire a doomed request (nor paint
        // its refusal as a comparison failure).
        const known = knownThreadStatus(threadId);
        if (known && known !== "open") return;

        const recordFailure = async (reason: unknown) => {
            // The backend refusing because the thread is no longer OPEN is
            // that same benign case, just discovered late (this view had no
            // status yet) — probe read-only and stay silent for closed ones.
            const summary = await fetchThreadSummary(threadId, {
                withFootprint: false,
            });
            if (summary && summary.status !== "open") return;
            catchUpFailures.value = {
                ...catchUpFailures.value,
                [threadId]: failureMessage(reason),
            };
            console.warn(
                `Could not catch thread ${threadId} up with main`,
                reason
            );
        };
        try {
            const { response, error } =
                await api.startThreadThreadMgtCatchUpStartPost({
                    body: { thread_id: threadId },
                });
            if (!response.ok) {
                await recordFailure(error);
                return;
            }
        } catch (error) {
            await recordFailure(error);
            return;
        }
        if (threadId in catchUpFailures.value) {
            const { [threadId]: _cleared, ...rest } = catchUpFailures.value;
            catchUpFailures.value = rest;
        }
        await refreshThread(threadId);
        void useWorktreeStore().sync();
    };

    /**
     * Answer a proposal. Confirm runs the saved plan right away on the backend
     * (falls back to an agent run if it can't); Cancel just records the answer.
     */
    const respondToProposal = async (
        threadId: string,
        entryId: string,
        response: "confirm" | "cancel"
    ) => {
        // Hide this proposal's approval buttons immediately on click, in sync with
        // the indicator below, rather than a round-trip later when the answer entry
        // lands.
        respondingProposalId.value = entryId;
        // Confirming runs the proposed actions as an agent turn. Show the waiting
        // indicator immediately — before the agent_started SSE arrives — so
        // Confirm has feedback until the change lands; agent_finished clears it.
        if (response === "confirm") {
            isWaitingForAgent.value = true;
            agentProgressSteps.value = [];
            if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = Date.now();
            }
        }
        try {
            await api.startThreadThreadMgtRespondToProposalStartPost({
                body: {
                    thread_id: threadId,
                    entry_id: entryId,
                    response,
                },
            });
        } catch (error) {
            // Don't leave a stuck spinner if the proposal couldn't be answered, and
            // bring the approval buttons back so the user can retry.
            if (response === "confirm") {
                isWaitingForAgent.value = false;
            }
            respondingProposalId.value = null;
            // Auto apply can answer the proposal between render and click. That
            // is the outcome the user wanted, so refresh quietly.
            if (!isAlreadyAnswered(error)) {
                toast.add({
                    severity: "error",
                    summary: "Could not answer the proposal",
                    detail: "It may already be answered — refreshing.",
                    life: 4000,
                });
            }
        }
        await refreshThread(threadId);
    };

    /** Turn auto apply on or off for the open thread. */
    // `threadId` is explicit because the draft bar can be showing a draft the
    // drawer has never opened — `activeThread` is only hydrated by fetchThread,
    // so defaulting to it silently no-opped the bar's Auto toggle in the main
    // flow (click Edit, draft auto-created, drawer never opened).
    const setAutoApply = async (enabled: boolean, threadId?: string) => {
        threadId ??= activeThread.value?.identity;
        if (!threadId) return;
        try {
            await api.startThreadThreadMgtSetAutoApplyStartPost({
                body: { thread_id: threadId, enabled },
            });
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not change auto mode",
                life: 4000,
            });
        }
        await refreshThread(threadId);
    };

    // Best-effort "user is typing" ping so the agent holds off while composing.
    const notifyTyping = async (threadId: string) => {
        try {
            await api.notifyTypingThreadTypingThreadIdPost({
                path: { thread_id: threadId },
            });
        } catch (e) {
            console.warn(`Typing ping failed for thread ${threadId}`, e);
        }
    };

    const createThread = async (
        title: string,
        {
            description,
            kind,
            subjectEntityType,
            subjectEntityId,
            contextItems,
            // Only the Edit-click path sets this: it says we opened the draft
            // on the user's behalf, which is what makes it erasable if nothing
            // ends up in it. Stored on the thread, so it survives a reload —
            // see Thread.auto_created on the backend.
            autoCreated = false,
            // A conversation about the working copy rather than a place to
            // stage changes. Listed apart from drafts and opened without
            // activating its branch — see Thread.is_discussion.
            isDiscussion = false,
            // Hold the agent back so the caller can attach files and pages
            // first, then call startThreadAgent.
            deferDispatch = false,
        }: {
            description?: string;
            kind?: types.ThreadKind;
            subjectEntityType?: string;
            subjectEntityId?: string;
            contextItems?: types.ThreadContextItem[];
            autoCreated?: boolean;
            isDiscussion?: boolean;
            deferDispatch?: boolean;
        } = {}
    ) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtCreateThreadStartPost({
                body: {
                    title,
                    description: description ?? null,
                    kind: kind ?? null,
                    subject_entity_type: subjectEntityType ?? null,
                    subject_entity_id: subjectEntityId ?? null,
                    context_items: contextItems ?? null,
                    auto_created: autoCreated,
                    is_discussion: isDiscussion,
                    defer_dispatch: deferDispatch,
                },
            });

        if (response.ok && data) {
            // Insert the created thread locally instead of refetching the whole
            // list. A full fetchThreads() recomputes affected_entities for every
            // open thread (an expensive per-thread VFS entity-path scan) and was
            // the main cost of thread-create latency. A brand-new thread affects
            // nothing yet, so the response is already complete.
            threads.value = [
                data,
                ...threads.value.filter((t) => t.identity !== data.identity),
            ];
            // Survive a `list_threads` that started before this create and
            // finishes after it — see createsInFlight.
            createsInFlight.set(data.identity, ++createSeq);
            toast.add({
                severity: "success",
                summary: "Thread created",
                detail: data.title,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not create thread",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    const updateThread = async (
        threadId: string,
        title: string,
        description: string | null
    ): Promise<ThreadSchema | undefined> => {
        isLoading.value = true;
        try {
            const { data, response } =
                await api.startThreadThreadMgtUpdateThreadStartPost({
                    body: {
                        thread_id: threadId,
                        title,
                        description,
                    },
                });
            if (!response.ok || !data) {
                toast.add({
                    severity: "error",
                    summary: "Could not update draft details",
                    detail: "Server returned " + response.status,
                });
                return undefined;
            }

            // `update_thread` never recomputes the branch diff — only
            // `get_thread`/`list_threads` do (see get_thread.py) — so
            // `data.affected_entities` is empty for any open thread regardless
            // of what it actually has staged. Keep whatever footprint we
            // already had, same as listThreads's withDiffs:false path, or a
            // rename knocks the draft out of "On this page" in the drawer.
            const previous = threads.value.find(
                (thread) => thread.identity === threadId
            );
            const knownFootprint = (
                previous ??
                (activeThread.value?.identity === threadId
                    ? activeThread.value
                    : undefined)
            )?.affected_entities;
            const updated: ThreadSchema =
                knownFootprint?.length && !data.affected_entities?.length
                    ? { ...data, affected_entities: knownFootprint }
                    : data;

            threads.value = previous
                ? threads.value.map((thread) =>
                      thread.identity === threadId ? updated : thread
                  )
                : [updated, ...threads.value];
            if (activeThread.value?.identity === threadId) {
                activeThread.value = updated;
            }
            return updated;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not update draft details",
                detail: "Please try again.",
            });
            return undefined;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Erase a draft that never became a decision — the only way a thread
     * record is ever removed, as opposed to resolved.
     *
     * The backend refuses anything meaningful (resolved, agent-kinded, with a
     * conversation, or with staged changes), so a wrong call here fails
     * loudly rather than destroying something. Returns true when the draft
     * actually went.
     */
    const deleteThread = async (threadId: string): Promise<boolean> => {
        try {
            const { response } =
                await api.startThreadThreadMgtDeleteThreadStartPost({
                    body: { thread_id: threadId },
                });
            if (!response.ok) return false;
            threads.value = threads.value.filter(
                (t) => t.identity !== threadId
            );
            if (activeThread.value?.identity === threadId) {
                activeThread.value = undefined;
                activeEntries.value = [];
            }
            return true;
        } catch {
            // The client throws on non-2xx. A refusal means the draft holds
            // something worth keeping, which is not an error worth shouting
            // about — the caller falls back to leaving it open.
            return false;
        }
    };

    // Creating a strategy-onboarding thread wakes the agent so it opens the chat.
    const startStrategyOnboarding = async () => {
        const data = await createThread("Draft Vision & Mission", {
            description:
                "Set up our Vision & Mission living document with the assistant.",
            kind: ThreadKind.STRATEGY_ONBOARDING,
        });
        if (data?.identity) {
            openDrawer(data.identity);
        }
        return data;
    };

    // The brief becomes the thread description, which is the opening
    // contribution the agent answers. Passing a kind wakes the agent at once.
    const startBusinessOnboarding = async (
        brief: string,
        contextItems?: types.ThreadContextItem[],
        attachments?: { files?: File[]; links?: string[] }
    ) => {
        // With attachments the thread is created quiet and started once they
        // have landed; otherwise the agent reads a thread whose files are not
        // there yet.
        const hasAttachments = Boolean(
            attachments?.files?.length || attachments?.links?.length
        );
        const data = await createThread("Set up my business", {
            description: brief,
            kind: ThreadKind.BUSINESS_ONBOARDING,
            contextItems,
            deferDispatch: hasAttachments,
        });
        if (!data?.identity) return data;

        openDrawer(data.identity);

        if (hasAttachments) {
            for (const file of attachments?.files ?? []) {
                await uploadThreadFile(data.identity, file);
            }
            for (const url of attachments?.links ?? []) {
                await attachThreadLink(data.identity, url);
            }
            await startThreadAgent(data.identity);
        }
        return data;
    };

    // Run the agent on a thread created with deferDispatch.
    const startThreadAgent = async (threadId: string) => {
        const { response } =
            await api.startThreadThreadMgtStartThreadAgentStartPost({
                body: { thread_id: threadId },
            });
        if (!response.ok) {
            toast.add({
                severity: "error",
                summary: "Could not start the assistant",
                detail: `Server returned ${response.status}`,
            });
            return;
        }
        await refreshThread(threadId);
        // Same optimistic flag the contribute path sets, so the drawer shows the
        // agent working without waiting for the agent_started SSE.
        isWaitingForAgent.value = true;
        if (agentRunStartedAt.value === null) {
            agentRunStartedAt.value = Date.now();
        }
    };

    /**
     * Thread whose findings should open as soon as they land. Set only for a run
     * the user started by hand, never for a weekly scan.
     */
    const pendingFindingsThreadId = ref<string | undefined>(undefined);

    const clearPendingFindings = () => {
        pendingFindingsThreadId.value = undefined;
    };

    const startMarketResearch = async () => {
        const data = await createThread("Research our market", {
            description:
                "Map our competitors and market trends with the assistant.",
            kind: ThreadKind.MARKET_RESEARCH,
        });
        if (data?.identity) {
            pendingFindingsThreadId.value = data.identity;
            openDrawer(data.identity);
        }
        return data;
    };

    const contribute = async (threadId: string, content: string) => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtContributeToThreadStartPost({
                body: {
                    thread_id: threadId,
                    content,
                    source_type: "user",
                },
            });

        if (response.ok && data) {
            await refreshThread(threadId);
            // A contribution dispatches the agent, so show "agent is working"
            // immediately instead of waiting for the agent_started SSE (agent_finished
            // clears it). Set after the fetch so the optimistic flag wins; generic
            // across every thread kind. Steps then stream in from the replay + SSE.
            isWaitingForAgent.value = true;
            if (agentRunStartedAt.value === null) {
                agentRunStartedAt.value = Date.now();
            }
        } else {
            toast.add({
                severity: "error",
                summary: "Could not contribute",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    // Files attached to the active thread (a FILE context item per upload). Every
    // viewer sees them; the thread agent reads them as context.
    const attachedFiles = computed(() =>
        (activeThread.value?.context_items ?? []).filter(
            (item) => item.kind === ThreadContextKind.FILE
        )
    );

    // Upload a file to a thread. The bytes land in the thread's VFS tree and a
    // FILE context item is appended; refetch so it shows for the uploader (other
    // viewers pick it up on their next thread fetch).
    const uploadThreadFile = async (threadId: string, file: File) => {
        isLoading.value = true;
        uploadingFileName.value = file.name;
        const origin = sourceOf(file);
        try {
            const { data, response } =
                await api.startThreadThreadMgtUploadThreadFileStartPost({
                    body: {
                        thread_id: threadId,
                        file_: file,
                        ...(origin && {
                            source: origin.source,
                            source_url: origin.url,
                        }),
                    },
                });
            if (response.ok && data) {
                await refreshThread(threadId);
                return data;
            }
            toast.add({
                severity: "error",
                summary: "Could not attach file",
                detail: "Server returned " + response.status,
            });
        } catch (e) {
            toast.add({
                severity: "error",
                summary: "Could not attach file",
                detail: String(e),
            });
        } finally {
            isLoading.value = false;
            uploadingFileName.value = null;
        }
    };

    // The backend fetches the page and stores its text as a FILE item, so from
    // here on it is indistinguishable from an upload.
    const attachThreadLink = async (threadId: string, url: string) => {
        isLoading.value = true;
        uploadingFileName.value = url;
        try {
            const { data, error, response } =
                await api.startThreadThreadMgtAttachThreadLinkStartPost({
                    body: { thread_id: threadId, url },
                });
            if (response.ok && data) {
                await refreshThread(threadId);
                return data;
            }
            // Only the backend knows whether it was blocked, missing or not a page.
            const detail = (error as { detail?: string } | undefined)?.detail;
            toast.add({
                severity: "error",
                summary: "Could not read that page",
                detail: detail ?? `Server returned ${response.status}`,
            });
        } catch (e) {
            toast.add({
                severity: "error",
                summary: "Could not read that page",
                detail: String(e),
            });
        } finally {
            isLoading.value = false;
            uploadingFileName.value = null;
        }
    };

    // Types safe to render in a tab because they cannot execute script. A blob
    // URL inherits THIS app's origin, so opening a file the uploader marked
    // text/html or image/svg+xml would run their markup as us (reading the
    // session from localStorage). The stored mimetype is whatever the uploading
    // client claimed and is never validated server-side, so anything not on this
    // list is saved rather than rendered.
    const INLINE_SAFE_MIMETYPES = new Set([
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/gif",
        "image/webp",
        "image/bmp",
        "text/plain",
    ]);

    const baseMimetype = (blob: Blob): string =>
        (blob.type || "").split(";")[0].trim().toLowerCase();

    // Open a thread attachment. The API needs the auth/org/stek headers, so a
    // plain link can't reach it — fetch the bytes, then hand a blob URL to a new
    // tab. Only for types that cannot carry script; everything else is saved,
    // because a blob URL of attacker-chosen HTML/SVG would execute as our origin.
    // The tab is opened synchronously so pop-up blockers, which require a user
    // gesture, don't kill it.
    const openThreadFile = async (
        threadId: string,
        fileId: string,
        filename: string
    ) => {
        const tab = window.open("", "_blank");
        const { data, response } =
            await api.startThreadThreadMgtGetThreadFileStartPost({
                body: { thread_id: threadId, file_id: fileId },
                parseAs: "blob",
            });
        if (!response.ok || !data) {
            tab?.close();
            toast.add({
                severity: "error",
                summary: "Could not open file",
                detail: "Server returned " + response.status,
            });
            return;
        }
        const blob = data as Blob;
        if (!INLINE_SAFE_MIMETYPES.has(baseMimetype(blob))) {
            // Not safe to render inline — save it instead of executing it.
            tab?.close();
            downloadBlob(URL.createObjectURL(blob), filename);
            return;
        }
        const url = URL.createObjectURL(blob);
        if (tab) {
            tab.location.href = url;
            // Keep the object URL alive long enough for the tab to load it.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else {
            // Pop-up blocked — save it so the click isn't lost.
            downloadBlob(url, filename);
        }
    };

    // Save a thread attachment to disk. Same authenticated fetch as `open`, but
    // always handed to an <a download>, which writes the file rather than
    // rendering it — so the uploader's mimetype cannot execute here.
    const downloadThreadFile = async (
        threadId: string,
        fileId: string,
        filename: string
    ) => {
        const { data, response } =
            await api.startThreadThreadMgtGetThreadFileStartPost({
                body: { thread_id: threadId, file_id: fileId },
                parseAs: "blob",
            });
        if (!response.ok || !data) {
            toast.add({
                severity: "error",
                summary: "Could not download file",
                detail: "Server returned " + response.status,
            });
            return;
        }
        downloadBlob(URL.createObjectURL(data as Blob), filename);
    };

    // Remove an attachment from a thread. The server allows this only for the
    // user who uploaded it; refetch so the strip drops the chip on success.
    const deleteThreadFile = async (threadId: string, fileId: string) => {
        try {
            const { response } =
                await api.startThreadThreadMgtDeleteThreadFileStartPost({
                    body: { thread_id: threadId, file_id: fileId },
                });
            if (response.ok) {
                await refreshThread(threadId);
                return;
            }
            toast.add({
                severity: "error",
                summary: "Could not remove file",
                detail: "Server returned " + response.status,
            });
        } catch (e) {
            toast.add({
                severity: "error",
                summary: "Could not remove file",
                detail: String(e),
            });
        }
    };

    // Creating a process-BPMN thread binds it to the process and wakes the agent
    // to model it from the user's description.
    const startProcessBpmnThread = async (
        processId: string,
        processName: string,
        description: string,
        onCreated?: () => void
    ) => {
        // Do the slow work while the launching dialog is still up: create the
        // thread, then post the description (which dispatches the agent and marks it
        // active server-side). Only THEN close the dialog (onCreated) and open the
        // drawer, so it reveals a thread that already has the description and the
        // agent working — no post-open flicker of the indicator. The drawer opens
        // WITHOUT hydrate data because `contribute` already fetched the full, current
        // thread into activeThread/activeEntries.
        const data = await createThread(`Build BPMN for ${processName}`, {
            kind: ThreadKind.PROCESS_BPMN,
            subjectEntityType: "process",
            subjectEntityId: processId,
            contextItems: [
                {
                    kind: ThreadContextKind.ENTITY,
                    data: { entity_type: "process", entity_id: processId },
                },
            ],
        });
        if (data?.identity) {
            await contribute(data.identity, description);
            onCreated?.();
            openDrawer(data.identity);
        }
        return data;
    };

    const resolveThread = async (
        threadId: string,
        action: "accept" | "reject",
        reason: string,
        why?: string
    ): Promise<ResolveOutcome> => {
        isLoading.value = true;
        try {
            await api.startThreadThreadMgtResolveThreadStartPost({
                body: {
                    thread_id: threadId,
                    action,
                    reason,
                    // Accept-only rationale (the "why" behind the change). For a
                    // reject the reason already carries the full explanation.
                    why: action === "accept" ? why?.trim() || null : null,
                },
            });
            // Patch the list entry here rather than waiting to be told. The
            // drawer's card renders `status`, and the only thing that used to
            // update it was the list refresh below — which is not awaited, and
            // takes as long as it takes. So a draft you had just discarded went
            // on reading "open" for tens of seconds.
            threads.value = threads.value.map((t) =>
                t.identity === threadId
                    ? {
                          ...t,
                          status: action === "accept" ? "accepted" : "rejected",
                      }
                    : t
            );

            // Clean accept/reject: refresh the active thread so the UI shows
            // the resolved status. The LIST refresh is deliberately not
            // awaited — it recomputes affected_entities for every open thread
            // (a VFS scan each), and holding the caller for it left the
            // resolve dialog spinning for seconds after the work had landed.
            await refreshThread(threadId);
            void fetchThreads();
            toast.add({
                severity: "success",
                summary: `Thread ${action}ed`,
                detail: reason,
                life: 3000,
            });
            return "resolved";
        } catch (error) {
            // A behind-main thread gates accept with a CONFLICT DomainError: the
            // backend already recorded the pick-a-side conflict records via the
            // forced catch-up, so refetch the thread to surface them (they ride on
            // `activeThread.conflicts` → the "Behind main" banner + resolution
            // panel) and route the caller into the resolution view. A reject's
            // partially-merged CONFLICT is NOT a pick-a-side case — fall through to
            // the error toast so it's surfaced rather than swallowed.
            if (action === "accept" && isConflictError(error)) {
                await refreshThread(threadId);
                return "conflict";
            }
            toast.add({
                severity: "error",
                summary: `Could not ${action} thread`,
                detail: domainErrorMessage(error) ?? "Unexpected error",
            });
            return "error";
        } finally {
            // Always clear loading — a thrown CONFLICT used to leave the spinner
            // hanging forever.
            isLoading.value = false;
        }
    };

    /**
     * Read a conflicted entity's content at base / master / branch for the
     * pick-a-side 3-way diff. Returns the three sides (or undefined on failure).
     */
    const readEntityVersions = async (
        threadId: string,
        entityType: string,
        entityId: string
    ): Promise<EntityVersionsSchema | undefined> => {
        const { data, response } =
            await api.startThreadThreadMgtReadEntityVersionsStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                },
            });

        if (response.ok && data) {
            return data;
        }
        toast.add({
            severity: "error",
            summary: "Could not load entity versions",
            detail: "Server returned " + response.status,
        });
        return undefined;
    };

    /**
     * Fetch the entities a user (not the agent) edited directly on the thread
     * branch, and cache them on `userChanges`. Called when the resolve dialog
     * opens so its "Changes made directly by users" section reflects the branch.
     * Returns the list (empty on failure or when there are no user edits).
     */
    const fetchUserChanges = async (
        threadId: string
    ): Promise<UserChangeSchema[]> => {
        const { data, response } =
            await api.startThreadThreadMgtListThreadUserChangesStartPost({
                body: { thread_id: threadId },
            });

        if (response.ok && data) {
            userChanges.value = data;
            return data;
        }
        userChanges.value = [];
        toast.add({
            severity: "error",
            summary: "Could not load user changes",
            detail: "Server returned " + response.status,
        });
        return [];
    };

    /**
     * Ask the backend for an LLM-suggested accept message built from the
     * thread's discussion + changes. Read-only: does not resolve the thread.
     * Returns null (and toasts) on failure so the caller can fall back to
     * manual entry — generation must never block accepting.
     */
    const generateAcceptMessage = async (
        threadId: string
    ): Promise<{ what: string; why: string } | null> => {
        try {
            const { data, response } =
                await api.startThreadThreadMgtGenerateAcceptMessageStartPost({
                    body: { thread_id: threadId },
                });
            if (response.ok && data) {
                return { what: data.what, why: data.why ?? "" };
            }
        } catch {
            // fall through to the toast + null
        }
        toast.add({
            severity: "error",
            summary: "Could not suggest a message",
            detail: "You can still write one manually.",
            life: 3000,
        });
        return null;
    };

    /**
     * Resolve one entity's catch-up conflict by picking a side (keep_main /
     * keep_thread / edited). Refetches the thread so the banner + per-conflict
     * rows reflect the new status (and accept unblocks once none stay pending).
     */
    const resolveConflict = async (
        threadId: string,
        entityType: string,
        entityId: string,
        resolution: ConflictResolution,
        content?: string
    ): Promise<ConflictRecordSchema | undefined> => {
        isLoading.value = true;
        const { data, response } =
            await api.startThreadThreadMgtResolveConflictStartPost({
                body: {
                    thread_id: threadId,
                    entity_type: entityType,
                    entity_id: entityId,
                    resolution,
                    content: content ?? null,
                },
            });

        if (response.ok && data) {
            await refreshThread(threadId);
            // The branch entity now holds the chosen version (keep_main /
            // keep_thread / edited). Refresh the viewed entity + worktree so it
            // updates live on the branch — without this, the description stays on
            // the pre-resolution value until a manual page refresh.
            notifyEntityChanged([{ entityType, entityId }]);
            void useWorktreeStore().sync();
            toast.add({
                severity: "success",
                summary: "Conflict resolved",
                detail: data.name || entityId,
                life: 3000,
            });
        } else {
            toast.add({
                severity: "error",
                summary: "Could not resolve conflict",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return response.ok ? data : undefined;
    };

    return {
        threads,
        activeThread,
        viewingThreadId,
        activeEntries,
        conflicts,
        pendingConflicts,
        resolvedConflicts,
        hasPendingConflicts,
        userChanges,
        isLoading,
        isFetching,
        isFetchingList,
        isWaitingForAgent,
        agentProgressSteps,
        agentRunStartedAt,
        liveCreatedEntities,
        respondingProposalId,
        drawerOpen,
        drawerThreadId,
        drawerLeaveAnimated,
        entityThreadMap,
        fetchThreads,
        fetchThread,
        fetchThreadSummary,
        readDraftState,
        refreshThreadFootprint,
        fetchNewEntries,
        createThread,
        updateThread,
        deleteThread,
        startStrategyOnboarding,
        startBusinessOnboarding,
        startThreadAgent,
        startMarketResearch,
        pendingFindingsThreadId,
        clearPendingFindings,
        startProcessBpmnThread,
        contribute,
        attachedFiles,
        uploadingFileName,
        uploadThreadFile,
        attachThreadLink,
        openThreadFile,
        downloadThreadFile,
        deleteThreadFile,
        respondToProposal,
        setAutoApply,
        notifyTyping,
        resolveThread,
        readEntityVersions,
        resolveConflict,
        fetchUserChanges,
        generateAcceptMessage,
        catchUpThread,
        catchUpFailures,
        connectToThread,
        disconnectThread,
        unreadTotal,
        markThreadRead,
        connectStekEvents,
        disconnectStekEvents,
        openDrawer,
        openThreadsList,
        closeDrawer,
        selectThread,
        getThreadsForEntity,
        hasThreadsForEntity,
    };
});

export const useThreadsManager = () => storeToRefs(useThreadsStore());
